// MapDiscoveryPage — UI for the automated map-finding pipeline.
// Phases:
//   1. Diff Analysis — drag/drop 2 bins, get ranked candidate map list
//   2. Heuristic Scan — single bin, background job + polling for top candidates
//   3. Claude Verify — send a candidate to Claude for typed identification
//   Verified maps saved to MongoDB verified_maps collection.

import { useState, useEffect, useRef } from 'react';
import { Upload, Loader2, CheckCircle2, AlertCircle, Cpu, Beaker, Save, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Reusable: drop / file input
// ─────────────────────────────────────────────────────────────────────────────
function FileInput({ label, onFile, file, testid }) {
  const ref = useRef(null);
  return (
    <label
      className="block p-4 border-2 border-dashed border-zinc-700 hover:border-emerald-400 rounded-sm cursor-pointer transition-colors bg-[#0A0A0A]"
      data-testid={`${testid}-label`}
    >
      <input
        ref={ref}
        type="file"
        accept=".bin"
        onChange={(e) => onFile(e.target.files?.[0] || null)}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      <div className="flex items-center gap-3">
        <Upload className="w-5 h-5 text-zinc-500" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
          <div className="text-sm text-white truncate font-['JetBrains_Mono']">
            {file ? file.name : 'Drop .bin or click to browse'}
          </div>
          {file && <div className="text-[10px] text-zinc-500">{(file.size / 1024).toFixed(1)} KB</div>}
        </div>
      </div>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Candidate row
// ─────────────────────────────────────────────────────────────────────────────
function CandidateRow({ candidate, idx, onVerify, verifying, verifyResult, onSave, savedIds }) {
  const addr = `0x${candidate.addr_start.toString(16).toUpperCase().padStart(6, '0')}`;
  const dims = candidate.likely_dimensions || candidate.dimensions || ['?', '?', '?'];
  const scaling = candidate.likely_scaling || candidate.scaling || '—';
  const conf = candidate.confidence ?? candidate.score ?? 0;
  const confPct = Math.round(conf * 100);
  const isVerified = verifyResult?.claude_result;
  const isSaved = savedIds?.has(addr);

  return (
    <div
      data-testid={`candidate-row-${idx}`}
      className="border border-[#27272A] rounded-sm bg-[#0F0F0F] hover:border-[#3a3a40] transition-colors"
    >
      <div className="grid grid-cols-12 gap-3 p-3 items-center text-xs">
        <div className="col-span-2 font-['JetBrains_Mono'] text-emerald-300">{addr}</div>
        <div className="col-span-1 text-zinc-400">{candidate.size}b</div>
        <div className="col-span-2 font-['JetBrains_Mono'] text-zinc-300">
          {dims[0]}×{dims[1]}×{dims[2]}
        </div>
        <div className="col-span-3 text-zinc-300 truncate" title={scaling}>{scaling}</div>
        <div className="col-span-1">
          <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-sm ${
            confPct >= 80 ? 'bg-emerald-500/20 text-emerald-300' :
            confPct >= 60 ? 'bg-amber-500/20 text-amber-300' :
            'bg-zinc-700 text-zinc-400'
          }`}>{confPct}%</span>
        </div>
        <div className="col-span-3 flex gap-2 justify-end">
          <button
            onClick={() => onVerify(candidate)}
            disabled={verifying}
            data-testid={`verify-btn-${idx}`}
            className="px-2.5 py-1 bg-[#007AFF]/20 hover:bg-[#007AFF]/40 disabled:opacity-50 text-[#4DA3FF] text-[10px] font-['JetBrains_Mono'] uppercase rounded-sm transition-colors"
          >
            {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify w/ AI'}
          </button>
          {isVerified && !isSaved && (
            <button
              onClick={() => onSave(candidate, verifyResult.claude_result)}
              data-testid={`save-btn-${idx}`}
              className="px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-300 text-[10px] font-['JetBrains_Mono'] uppercase rounded-sm transition-colors"
            >
              <Save className="w-3 h-3" />
            </button>
          )}
          {isSaved && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
        </div>
      </div>
      {verifyResult?.claude_result && (
        <div className="px-3 pb-3 pt-1 border-t border-[#1f1f23] bg-[#0a0a0a]">
          {verifyResult.claude_result.identified_as ? (
            <div className="text-[11px] space-y-1.5">
              <div>
                <span className="text-zinc-500">Identified as: </span>
                <span className="font-['JetBrains_Mono'] text-amber-300 font-bold">
                  {verifyResult.claude_result.identified_as}
                </span>
                <span className="text-zinc-500"> · confidence: </span>
                <span className="text-amber-200">{verifyResult.claude_result.confidence}</span>
              </div>
              <div className="text-zinc-300">{verifyResult.claude_result.reasoning}</div>
              {verifyResult.claude_result.concerns?.length > 0 && (
                <div className="text-amber-400">
                  ⚠ {verifyResult.claude_result.concerns.join(' · ')}
                </div>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-rose-400">
              AI response failed: {verifyResult.claude_result.error || 'parse failed'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function MapDiscoveryPage() {
  const [phase, setPhase] = useState(1);
  // Phase 1
  const [binA, setBinA] = useState(null);
  const [binB, setBinB] = useState(null);
  // Phase 2
  const [scanBin, setScanBin] = useState(null);
  const [scanJob, setScanJob] = useState(null); // {job_id, status, result?}
  // Shared
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verifyingIdx, setVerifyingIdx] = useState(null);
  const [verifyResults, setVerifyResults] = useState({});
  const [savedIds, setSavedIds] = useState(new Set());
  const [ecuId, setEcuId] = useState('MSV80_K80S');

  // Poll scan job status
  useEffect(() => {
    if (!scanJob?.job_id || scanJob.status !== 'running') return;
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/map-discovery/scan-status/${scanJob.job_id}`);
        const data = await res.json();
        setScanJob(data);
        if (data.status === 'complete') {
          setCandidates(data.result?.candidates || []);
          clearInterval(id);
        } else if (data.status === 'error') {
          setError(data.error);
          clearInterval(id);
        }
      } catch (e) {
        setError(e.message);
        clearInterval(id);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [scanJob]);

  const runDiff = async () => {
    if (!binA || !binB) { setError('Need both bins'); return; }
    setLoading(true); setError(null); setCandidates([]); setVerifyResults({});
    try {
      const fd = new FormData();
      fd.append('bin_a', binA);
      fd.append('bin_b', binB);
      const res = await fetch(`${API}/api/map-discovery/diff-analysis`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCandidates(data.candidates || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const runScan = async () => {
    if (!scanBin) { setError('Need a bin to scan'); return; }
    setLoading(true); setError(null); setCandidates([]); setVerifyResults({}); setScanJob(null);
    try {
      const fd = new FormData();
      fd.append('bin_file', scanBin);
      fd.append('stride', '32');
      fd.append('min_score', '0.80');
      fd.append('top_n', '30');
      const res = await fetch(`${API}/api/map-discovery/scan`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setScanJob(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyCandidate = async (candidate) => {
    const idx = candidates.indexOf(candidate);
    setVerifyingIdx(idx);
    const dims = candidate.likely_dimensions || candidate.dimensions;
    const scaling = candidate.likely_scaling || candidate.scaling;
    const sourceBin = phase === 1 ? binB : scanBin;
    if (!sourceBin || !dims || !scaling) { setVerifyingIdx(null); return; }
    try {
      const fd = new FormData();
      fd.append('bin_file', sourceBin);
      fd.append('addr', candidate.addr_start);
      fd.append('rows', dims[0]);
      fd.append('cols', dims[1]);
      fd.append('cellsize', dims[2]);
      fd.append('scaling', scaling);
      const res = await fetch(`${API}/api/map-discovery/verify`, { method: 'POST', body: fd });
      const data = await res.json();
      setVerifyResults((prev) => ({ ...prev, [idx]: data }));
    } catch (e) {
      setVerifyResults((prev) => ({ ...prev, [idx]: { error: e.message } }));
    } finally {
      setVerifyingIdx(null);
    }
  };

  const saveVerified = async (candidate, claudeResult) => {
    const dims = candidate.likely_dimensions || candidate.dimensions;
    const scaling = candidate.likely_scaling || candidate.scaling;
    try {
      const res = await fetch(`${API}/api/map-discovery/save-verified`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ecu_id: ecuId,
          name: claudeResult.identified_as || 'unidentified',
          addr: candidate.addr_start,
          rows: dims[0], cols: dims[1], cellsize: dims[2],
          scaling, byte_order: 'BE',
          confidence: claudeResult.confidence || 'medium',
          source: 'claude_verified',
          notes: claudeResult.reasoning || '',
        }),
      });
      if (res.ok) {
        const addrHex = `0x${candidate.addr_start.toString(16).toUpperCase().padStart(6, '0')}`;
        setSavedIds((prev) => new Set([...prev, addrHex]));
      }
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100 p-6 font-['Inter']">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="font-['Outfit'] text-4xl font-black tracking-tighter mb-2">
            Map Discovery
          </h1>
          <p className="text-sm text-zinc-400 max-w-2xl">
            Automated ECU map finding. Diff two bins to see what a tuner changed (Phase 1),
            scan one bin for candidate maps (Phase 2), or send a candidate to AI for
            identification (Phase 3). Verified maps build into a permanent database.
          </p>
        </div>

        {/* Phase selector */}
        <div className="flex gap-2 mb-5">
          {[
            { n: 1, label: '1 · Diff Analysis', icon: Beaker },
            { n: 2, label: '2 · Heuristic Scan', icon: Cpu },
          ].map((p) => (
            <button
              key={p.n}
              onClick={() => { setPhase(p.n); setCandidates([]); setVerifyResults({}); setError(null); setScanJob(null); }}
              data-testid={`phase-tab-${p.n}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-['JetBrains_Mono'] uppercase tracking-wider rounded-sm transition-colors ${
                phase === p.n
                  ? 'bg-emerald-500 text-[#0A0A0A] font-bold'
                  : 'bg-[#161616] text-zinc-400 hover:bg-[#202024]'
              }`}
            >
              <p.icon className="w-3.5 h-3.5" />{p.label}
            </button>
          ))}
        </div>

        {/* ECU id selector */}
        <div className="mb-5 flex items-center gap-3 text-xs">
          <label className="text-zinc-500 font-['JetBrains_Mono'] uppercase tracking-wider">ECU ID:</label>
          <input
            value={ecuId}
            onChange={(e) => setEcuId(e.target.value)}
            data-testid="ecu-id-input"
            className="px-2 py-1 bg-[#0F0F0F] border border-[#27272A] text-white font-['JetBrains_Mono'] rounded-sm focus:outline-none focus:border-emerald-400 w-40"
          />
          <span className="text-zinc-600 text-[10px]">verified maps saved against this ECU</span>
        </div>

        {/* Phase content */}
        {phase === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <FileInput label="Bin A · baseline (factory stock)" onFile={setBinA} file={binA} testid="bin-a" />
            <FileInput label="Bin B · modified (e.g., V11_inj550)" onFile={setBinB} file={binB} testid="bin-b" />
            <button
              onClick={runDiff}
              disabled={loading || !binA || !binB}
              data-testid="run-diff-btn"
              className="md:col-span-2 px-5 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0A0A0A] font-bold text-xs uppercase tracking-wider rounded-sm transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              Run Diff Analysis
            </button>
          </div>
        )}

        {phase === 2 && (
          <div className="grid grid-cols-1 gap-3 mb-4">
            <FileInput label="Bin to scan" onFile={setScanBin} file={scanBin} testid="scan-bin" />
            <button
              onClick={runScan}
              disabled={loading || !scanBin}
              data-testid="run-scan-btn"
              className="px-5 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0A0A0A] font-bold text-xs uppercase tracking-wider rounded-sm transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              Start Heuristic Scan (background)
            </button>
            {scanJob?.status === 'running' && (
              <div data-testid="scan-progress" className="px-4 py-3 bg-[#161616] border border-[#27272A] rounded-sm text-xs text-zinc-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                Scan running… job <span className="font-['JetBrains_Mono'] text-emerald-300">{scanJob.job_id}</span> · polling every 2s
              </div>
            )}
          </div>
        )}

        {error && (
          <div data-testid="error-banner" className="mb-4 p-3 bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs rounded-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* Candidates table */}
        {candidates.length > 0 && (
          <div data-testid="candidates-table" className="space-y-1">
            <div className="grid grid-cols-12 gap-3 px-3 py-2 text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500">
              <div className="col-span-2">Address</div>
              <div className="col-span-1">Size</div>
              <div className="col-span-2">Dimensions</div>
              <div className="col-span-3">Scaling</div>
              <div className="col-span-1">Conf</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {candidates.map((c, i) => (
              <CandidateRow
                key={i} idx={i} candidate={c}
                onVerify={verifyCandidate} verifying={verifyingIdx === i}
                verifyResult={verifyResults[i]} onSave={saveVerified}
                savedIds={savedIds}
              />
            ))}
            <div className="mt-3 text-[10px] text-zinc-600 font-['JetBrains_Mono']">
              {candidates.length} candidates · click <span className="text-[#4DA3FF]">Verify w/ AI</span> to identify each map · save verified maps to build the ECU&apos;s known-good database.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
